import { groupBy, partition, sortBy } from 'lodash/fp';
import { Fragment } from 'react';

import BankStatementVerification from 'dashboard/components/BankStatementVerification';
import VerificationDetail from 'dashboard/components/VerificationDetail';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { BankStatementDataField, Document, DocumentGeneric } from 'dashboard/models/document';
import { EnvironmentType } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { Verification, VerificationDocument } from 'dashboard/models/verification';
import Card from 'design-system/components/Card';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

interface InquiryVerificationsProps {
  inquiry: Inquiry;
  verifications: Verification[];
  documents: Document[];
  featureFlags: FeatureFlagsMap;
  environmentType: EnvironmentType;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
}

const EmptyVerifications = () => {
  return (
    <Card>
      <Card.Body>
        <Card.Title>Verifications</Card.Title>
        <TableNullState hasUsedFilters={false} dataTestBlock='inquiry-verifications'>
          <h3>Looks like there aren&apos;t any verifications for this inquiry yet.</h3>
        </TableNullState>
      </Card.Body>
    </Card>
  );
};

const InquiryVerifications = ({
  inquiry,
  verifications,
  documents,
  featureFlags,
  environmentType,
  handleFlagVerificationIssues,
}: InquiryVerificationsProps) => {
  if (verifications.length === 0) {
    return <EmptyVerifications />;
  }

  const sortedVerifications = sortBy((v) => v.attributes['created-at'], verifications);
  // To reduce the need to scroll on initial viewing, we collapse all but the last verification of each type present
  // PER-5508
  const verificationsByType = Object.entries(groupBy(({ type }) => type, sortedVerifications));

  return (
    <>
      {verificationsByType.map(([type, verifications], index) => {
        // Verifications for Bank Statements handled differently
        const bankStatementDocuments: DocumentGeneric[] = [];
        const [bankStatementVerifications, otherVerifications] =
          featureFlags['documents:dashboard-show-credit-analysis'] &&
          type === ApiModel.VerificationDocument
            ? partition((v) => {
                const document = documents.find(
                  (doc): doc is DocumentGeneric => v.relationships.document?.data.id === doc.id,
                );

                if (document && BankStatementDataField in document.attributes.fields) {
                  bankStatementDocuments.push(document);
                  return true;
                }
                return false;
              }, verifications)
            : [[], verifications];

        return (
          <Fragment key={index}>
            {bankStatementVerifications.length > 0 && (
              <BankStatementVerification
                inquiry={inquiry}
                verifications={bankStatementVerifications as VerificationDocument[]}
                documents={bankStatementDocuments}
              />
            )}
            {otherVerifications.map((v, i) => {
              const isDefaultOpen = verifications.length === 1 || verifications.length === i + 1;

              return (
                <VerificationDetail
                  key={v.id}
                  verification={v}
                  featureFlags={featureFlags}
                  isDefaultOpen={isDefaultOpen}
                  documents={documents}
                  environmentType={environmentType}
                  exposeId={true}
                  handleFlagVerificationIssues={handleFlagVerificationIssues}
                />
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
};

export default InquiryVerifications;
